import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Field from './Field';
import { borderLabel, useLabelPlacement } from './labelPlacement';
import Calendar from './Calendar';
import {
  addDays, addMonths, autoSlash, clampDay, formatMonthYear, formatUs, formatUsShort, inRange, parseMonthYear, parseUs,
  startOfMonth, toDay, toIsoDate, today, type TwoDigitYear,
} from './dateUtils';
import { describedBy, inputAriaLabel, inputAttrProps, isInvalid, useFieldIds, type BaseFieldProps } from './fieldUtils';
import './form.css';

/**
 * Date field: typed MM/DD/YYYY entry plus our own calendar popup.
 *
 * Values are what DateBox callers store. 79 of the 95 DateBox uses pass
 * dateSerializationFormat="yyyy-MM-dd", so the default is a "yyyy-MM-dd"
 * string; `valueType="date"` reports a local-midnight Date instead. Either
 * kind is accepted as `value`, `min` and `max`; a "yyyy-MM-ddTHH:mm:ss" value
 * is read as its day.
 *
 * onValueChanged fires only when a person changes the date (typing then Enter
 * or leaving the field, picking a day, clearing, stepping), never when the
 * `value` prop changes. Enter only accepts the date: in a dialog it does not
 * also press Save (Ctrl+Enter does).
 *
 * Keyboard: Alt+Down, F4 or the calendar button opens the calendar (F4 and
 * Alt+Down are the desktop DateEdit's keys); Escape, Alt+Up or F4 closes it
 * and focus returns to the field. While it is open, Tab and Shift+Tab go round
 * the calendar's buttons and days (it is a dialog) instead of leaving it open
 * behind. Typed text that is not a real day, or is outside min/max, is kept
 * and flagged rather than silently changed.
 *
 * The calendar has the desktop DateEdit's footer: Today (This month in
 * `monthOnly`), off when today is outside min/max, and Clear when the field
 * may be emptied (`showClearButton`).
 *
 * Typing digits at the end puts the slashes in ("08101967" shows 08/10/1967,
 * "8" shows "08/"; see autoSlash). Backspace, pasting and edits in the middle
 * are left exactly as typed and read when the field is left.
 *
 * `monthOnly` (Reports' Weight - Matrix, DateBox "MMMM yyyy" with calendar
 * zoom level "year"): shows "October 2026", takes "October 2026", "Oct 26" or
 * "10/2026", opens the calendar on the month grid, steps by a month, and
 * reports the 1st of the month.
 */
interface DateFieldCommon extends BaseFieldProps {
  value: string | Date | null | undefined;
  min?: string | Date | null;
  max?: string | Date | null;
  /** Show a clear button while there is a date. Off by default, as DateBox. */
  showClearButton?: boolean;
  /** Previous / next day buttons either side of the field (the Reports pattern).
   *  In `monthOnly` mode they step a month. */
  showStepButtons?: boolean;
  /** Pick a month, not a day: the value is the 1st of the month. */
  monthOnly?: boolean;
  /** "MM/DD/YYYY" (default) or "M/D/YYYY" (DateBox "M/d/yyyy"). Typing accepts both. */
  displayFormat?: 'MM/DD/YYYY' | 'M/D/YYYY';
  /** How a typed two-digit year is read: 2000s (default) or 'past' for birth
   *  dates (08/10/67 is 1967). */
  twoDigitYear?: TwoDigitYear;
  /** Shown instead of "Enter a date on or before ..." when a typed date is
   *  outside min/max (e.g. "Birth date can't be in the future."). */
  rangeErrorText?: string;
  /** MUI's field size: 'small' (default, every form) or 'medium' (the phone
   *  sheets' 56px fields; pass `height={56}` with it). */
  size?: 'small' | 'medium';
  onOpened?: () => void;
  onClosed?: () => void;
}

interface DateFieldString extends DateFieldCommon {
  valueType?: 'string';
  /** "yyyy-MM-dd", or null when cleared. User changes only. */
  onValueChanged: (value: string | null) => void;
}

interface DateFieldDate extends DateFieldCommon {
  valueType: 'date';
  /** Local midnight, or null when cleared. User changes only. */
  onValueChanged: (value: Date | null) => void;
}

export type DateFieldProps = DateFieldString | DateFieldDate;

const POPUP_MODIFIERS = [{ name: 'preventOverflow', options: { padding: 8 } }];

export default function DateField(props: DateFieldProps) {
  const {
    value, min, max, label, ariaLabel, required, disabled, readOnly, helperText, errorText,
    width, height, className, inputAttr, showClearButton = false, showStepButtons = false, onOpened, onClosed, labelPlacement,
    monthOnly = false, displayFormat = 'MM/DD/YYYY', twoDigitYear = '2000s', rangeErrorText, size = 'small',
  } = props;
  const placeholder = props.placeholder ?? (monthOnly ? 'Month YYYY' : 'MM/DD/YYYY');
  const ids = useFieldIds(inputAttr);
  const day = toDay(value);
  const minD = toDay(min);
  const maxD = toDay(max);
  // Month mode compares and reports months; the earliest allowed month is the
  // one min falls in.
  const keyOf = (d: Date | null) => (d ? toIsoDate(monthOnly ? startOfMonth(d) : d) : '');
  const format = monthOnly ? formatMonthYear : displayFormat === 'M/D/YYYY' ? formatUsShort : formatUs;
  const parse = monthOnly ? parseMonthYear : (t: string) => parseUs(t, twoDigitYear);
  const minCheck = monthOnly && minD ? startOfMonth(minD) : minD;
  const key = keyOf(day);
  const unit = monthOnly ? 'month' : 'day';

  // What the person is typing, tied to the value it was typed over: when the
  // value changes from outside, the stale draft and its error are dropped.
  const [draft, setDraft] = useState<{ text: string; forKey: string; error?: string } | null>(null);
  const live = draft && draft.forKey === key ? draft : null;
  const text = live ? live.text : day ? format(day) : '';
  const typedError = live?.error;

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popupId = `${ids.id}-calendar`;
  const editable = !disabled && !readOnly;

  const emit = (d: Date | null) => {
    if (props.valueType === 'date') props.onValueChanged(d);
    else props.onValueChanged(d ? toIsoDate(d) : null);
  };

  const rangeMessage = () => {
    if (rangeErrorText) return rangeErrorText;
    const what = monthOnly ? 'month' : 'date';
    if (minD && maxD) return `Enter a ${what} from ${format(minD)} to ${format(maxD)}`;
    if (minD) return `Enter a ${what} on or after ${format(minD)}`;
    return `Enter a ${what} on or before ${format(maxD!)}`;
  };

  /** Read the typed text: emit a real, in-range change; otherwise flag it. */
  const commit = () => {
    if (!live) return;
    const t = live.text.trim();
    if (!t) {
      setDraft(null);
      if (day) emit(null);
      return;
    }
    const parsed = parse(t);
    if (!parsed) { setDraft({ ...live, error: monthOnly ? 'Enter a month as Month YYYY' : 'Enter a date as MM/DD/YYYY' }); return; }
    if (!inRange(parsed, minCheck, maxD)) { setDraft({ ...live, error: rangeMessage() }); return; }
    setDraft(null);
    if (keyOf(parsed) !== key) emit(parsed);
  };

  const openCalendar = () => {
    if (!editable || open) return;
    commit();
    setOpen(true);
    onOpened?.();
  };

  const closeCalendar = (refocus: boolean) => {
    if (!open) return;
    setOpen(false);
    onClosed?.();
    if (refocus) inputRef.current?.focus();
  };

  const pickDay = (d: Date) => {
    setDraft(null);
    if (keyOf(d) !== key) emit(d);
    closeCalendar(true);
  };

  const clear = () => {
    setDraft(null);
    if (day) emit(null);
  };

  // The calendar is a dialog: Tab and Shift+Tab go round its buttons and the
  // one tabbable day (the WAI-ARIA date picker dialog). Before, Tab left the
  // popup open behind and focus went to the end of the page (or the top of a
  // dialog).
  const onPopupKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    // Sorted: a selector list is not always returned in page order (jsdom).
    const stops = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), [tabindex="0"]'))
      .filter((el) => !el.closest('[inert]'))
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    if (stops.length === 0) return;
    const at = document.activeElement;
    const target = e.shiftKey ? (at === stops[0] ? stops[stops.length - 1] : null) : (at === stops[stops.length - 1] ? stops[0] : null);
    if (target) {
      e.preventDefault();
      target.focus();
    }
  };

  // The day (or month) a step lands on, or null when there is nowhere to go.
  const stepTarget = (n: number): Date | null => {
    if (monthOnly) {
      const next = addMonths(startOfMonth(day ?? today()), n);
      return inRange(next, minCheck, maxD) ? next : null;
    }
    const next = clampDay(addDays(day ?? today(), n), minD, maxD);
    return day && keyOf(next) === key ? null : next;
  };

  // A month past either bound is not a step (Reports' month spinner); a day
  // step is clamped to min/max. The buttons are disabled when there is
  // nowhere to go.
  const stepDay = (n: number) => {
    const next = stepTarget(n);
    if (!next) return;
    setDraft(null);
    if (keyOf(next) !== key) emit(next);
  };

  // One character typed at the end of the text (not a paste, not Backspace,
  // not an edit in the middle) gets its slashes put in; anything else is kept
  // as typed, so the caret never jumps and deleting a slash just deletes it.
  const onTextChange = (input: HTMLInputElement) => {
    let next = input.value;
    if (!monthOnly && next.length === text.length + 1 && next.startsWith(text) && input.selectionStart === next.length) {
      next = autoSlash(next, displayFormat !== 'M/D/YYYY') ?? next;
    }
    setDraft({ text: next, forKey: key });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.altKey && e.key === 'ArrowDown') || (e.key === 'F4' && !open)) { e.preventDefault(); openCalendar(); }
    else if (open && (e.key === 'Escape' || e.key === 'F4' || (e.altKey && e.key === 'ArrowUp'))) {
      // Closing the calendar is all Escape does here: a dialog around the
      // field stays open.
      e.preventDefault();
      e.stopPropagation();
      closeCalendar(true);
    } else if (e.key === 'Enter') {
      commit();
      // Enter here only accepts the typed date: it never also reaches a dialog's
      // Enter = Save, which could run before the new date is committed and save the
      // old one. Ctrl+Enter still saves (useDialogKeys waits a tick for the commit).
      if (editable && !e.ctrlKey && !e.metaKey) { e.preventDefault(); e.stopPropagation(); }
    }
  };

  const invalid = isInvalid(props) || !!typedError;
  const shownError = errorText ?? typedError;
  const accessibleName = inputAriaLabel(label, required, ariaLabel);
  const placement = useLabelPlacement(labelPlacement);
  const outlineLabel = borderLabel(placement, label, required);
  // A popup inside a MUI dialog must render inside it, or the dialog's focus
  // trap pulls focus straight back out of the calendar.
  const container = () => anchorRef.current?.closest<HTMLElement>('.MuiDialog-root, .MuiModal-root') ?? document.body;

  const field = (
    <TextField
      id={ids.id}
      className="app-date-input"
      value={text}
      onChange={(e) => onTextChange(e.target as HTMLInputElement)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      error={invalid}
      size={size}
      fullWidth
      label={outlineLabel}
      InputLabelProps={outlineLabel ? { shrink: true } : undefined}
      inputRef={inputRef}
      InputProps={{
        ref: anchorRef,
        notched: !!outlineLabel,
        readOnly,
        endAdornment: (
          <InputAdornment position="end">
            {showClearButton && editable && day && (
              <IconButton size="small" aria-label={`Clear ${label ?? ariaLabel ?? 'date'}`} onClick={clear} edge={false}>
                <i className="bi bi-x-lg" aria-hidden="true" style={{ fontSize: 12 }} />
              </IconButton>
            )}
            <IconButton
              size="small"
              edge="end"
              aria-label={`Choose ${monthOnly ? 'month' : 'date'}${label ?? ariaLabel ? `, ${label ?? ariaLabel}` : ''}`}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-controls={open ? popupId : undefined}
              disabled={!editable}
              onClick={() => (open ? closeCalendar(true) : openCalendar())}
            >
              <i className="bi bi-calendar3" aria-hidden="true" style={{ fontSize: 14 }} />
            </IconButton>
          </InputAdornment>
        ),
      }}
      inputProps={{
        ...inputAttrProps(inputAttr),
        inputMode: monthOnly ? 'text' : 'numeric',
        autoComplete: 'off',
        'aria-label': accessibleName,
        'aria-required': required || undefined,
        'aria-invalid': invalid || undefined,
        'aria-describedby': describedBy(ids, { helperText, errorText: shownError }),
      }}
    />
  );

  return (
    <Field
      inputId={ids.id}
      label={label}
      required={required}
      helperText={helperText}
      helperId={ids.helperId}
      errorText={shownError}
      errorId={ids.errorId}
      error={invalid}
      disabled={disabled}
      width={width}
      height={height}
      labelPlacement={placement}
      className={`${readOnly ? 'app-field--readonly ' : ''}${className ?? ''}`.trim() || undefined}
    >
      {showStepButtons ? (
        <div className="app-date-row">
          <IconButton size="small" aria-label={`Previous ${unit}, ${label ?? ariaLabel ?? 'date'}`} disabled={!editable || !stepTarget(-1)} onClick={() => stepDay(-1)}>
            <i className="bi bi-chevron-left" aria-hidden="true" style={{ fontSize: 'var(--font-size-base)' }} />
          </IconButton>
          {field}
          <IconButton size="small" aria-label={`Next ${unit}, ${label ?? ariaLabel ?? 'date'}`} disabled={!editable || !stepTarget(1)} onClick={() => stepDay(1)}>
            <i className="bi bi-chevron-right" aria-hidden="true" style={{ fontSize: 'var(--font-size-base)' }} />
          </IconButton>
        </div>
      ) : field}
      {/* 8px off the screen's edge: on a phone the calendar is nearly as wide as the screen. */}
      <Popper open={open} anchorEl={() => anchorRef.current!} placement="bottom-start" className="app-cal-popup" container={container}
        modifiers={POPUP_MODIFIERS}>
        <ClickAwayListener onClickAway={(e) => { if (!anchorRef.current?.contains(e.target as Node)) closeCalendar(false); }}>
          {/* Tab reaches the calendar's buttons and days inside; the dialog itself is not a control. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div id={popupId} role="dialog" aria-label={`Choose ${label ?? ariaLabel ?? 'date'}`} onKeyDown={onPopupKeyDown}>
            <Calendar
              value={day}
              onSelect={pickDay}
              min={minD}
              max={maxD}
              focusOnMount
              onEscape={() => closeCalendar(true)}
              pick={monthOnly ? 'month' : 'day'}
              showToday
              onClear={showClearButton && editable ? () => { clear(); closeCalendar(true); } : undefined}
            />
          </div>
        </ClickAwayListener>
      </Popper>
    </Field>
  );
}
